import React, { useMemo, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { FormSpy } from 'react-final-form';
import { Grid, makeStyles } from '@material-ui/core';
import { useNotify, FormDataConsumer, SaveButton } from 'react-admin';
import { defaultTo } from 'lodash';
import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import SimpleFormWithSave from '../../../core/components/form/SimpleFormWithSave';
import EmailForm from '../../resourceuploads/uploadsteps/emailDialog/EmailForm';
import DistributionListApi from '../../../core/apis/DistributionListApi';
import DistributionListContactDialog from '../../testemail/DistributionListContactDialog';
import EmailPreviewDialog from './EmailPreviewDialog';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import LogApi from '../../../../src/core/apis/LogApi';
import { getIcError } from '../../../../src/core/providers/utils';
import Confirm from '../../../core/components/forked/layout/Confirm';

const useStyles = makeStyles({
  paperWidthFalse: {
    height: 'inherit'
  },
  primaryButton: {
    border: '1px solid #E0E0E0',
    '& span': {
      padding: 0
    }
  },
  secondaryButton: {
    border: '1px solid #E0E0E0',
    '& span': {
      padding: 0
    },
    marginLeft: '10px'
  },
  form: {
    height: 'inherit',
    '& > div': {
      padding: '0px !important',
      height: 'inherit'
    },
    '& > div > div': {
      display: 'flex',
      flexDirection: 'column',
      height: 'inherit'
    }
  },
  leftWrapper: {
    flexGrow: 1
  }
});

const splitEmails = value => {
  if (value && value.length > 0) {
    const emailParts = value.split(';');
    const mailDto = [];
    for (var i = 0; i <= emailParts.length - 1; i++) {
      var emailAddress = emailParts[i].trim();
      if (emailAddress !== '') {
        mailDto.push({
          name: null,
          address: emailAddress
        });
      }
    }
    return mailDto;
  }
  return [];
};

const getResourceUploadId = filters => {
  const resourceuploadId =
    filters &&
    filters[0] &&
    filters[0].resourceUploadIds &&
    filters[0].resourceUploadIds.length === 1
      ? filters[0].resourceUploadIds[0]
      : null;
  if (resourceuploadId === null) {
    return null;
  }
  for (let f of filters) {
    if (f && f.resourceUploadIds && f.resourceUploadIds.length === 1) {
      if (resourceuploadId !== f.resourceUploadIds[0]) {
        return null;
      }
    }
  }
  return resourceuploadId;
};

const EmailDialog = props => {
  const [
    showDistributionListTestEmailDialog,
    setShowDistributionListTestEmailDialog
  ] = useState(false);

  const [
    showDistributionListPreviewEmailDialog,
    setShowDistributionListPreviewEmailDialog
  ] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [savedFormProps, setSavedFormProps] = useState(null);
  const [openBanner, setOpenBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const classes = useStyles();
  const {
    handleClose,
    showDialog,
    choices,
    recipients,
    templateId,
    distributionListName = 'Unsaved Distribution List',
    variableType = 'distributionList',
    distributionListId,
    userProfile,
    dlOptions
  } = props;

  const initRecipients = useMemo(() => {
    const initRecipients = [];
    recipients.forEach(recipient => {
      const choice = choices.find(
        obj => obj.userId === recipient.userId && obj.fundId === recipient.fund
      );
      if (choice) {
        if (recipient.includeAllCcEmails) {
          initRecipients.push(choice);
        } else {
          initRecipients.push({ ...choice, ccEmails: [] });
        }
      }
    });
    return initRecipients;
  }, [choices, recipients]);

  function getUniqueInvestors(selectedItems) {
    const uniqueInvestors = new Map();

    selectedItems.forEach(item => {
      item.investors.forEach(investor => {
        uniqueInvestors.set(investor.id, investor.name);
      });
    });

    return Array.from(uniqueInvestors, ([investorId, investorName]) => ({
      investorId,
      investorName
    }));
  }

  const handleOpenBanner = formProps => {
    let investors = getUniqueInvestors(formProps.selectedItems);

    const hasAllInvestors = investors.some(
      investor =>
        investor.investorName &&
        investor.investorName.toLowerCase() === 'all investors'
    );
    const hasMultipleInvestors = investors.length > 1;
    const hasAttachments =
      formProps.attachments !== undefined && formProps.attachments.length > 0;

    if (hasAllInvestors || hasMultipleInvestors || hasAttachments) {
      setSavedFormProps(formProps);
      setOpenBanner(true);
    } else {
      handleSubmit(formProps);
    }
  };

  const handleCloseBanner = () => {
    setSavedFormProps(null);
    setOpenBanner(false);
  };

  const handleSendClick = formProps => {
    LogApi.sendClientlogs(
      LogApi.createTrackingObject(
        null,
        null,
        `distribution list id: ${formProps?.record?.id}, name: ${formProps?.record?.name}`,
        null,
        null,
        `POST v1/distributionlist/mail`,
        `Distribution List Send Email Button Handler`
      )
    ).catch(err => {
      console.error('Server error: ' + getIcError(err));
    });

    formProps.form.submit();
  };

  const handleSubmit = async values => {
    setIsLoading(true);
    LogApi.sendClientlogs(
      LogApi.createTrackingObject(
        null,
        null,
        `distribution list id: ${values?.id}, name: ${values?.name}`,
        null,
        null,
        `POST v1/distributionlist/mail`,
        `Distribution List Form Submit Handler`
      )
    ).catch(err => {
      console.error('Server error: ' + getIcError(err));
    });

    setSubmitting(true);
    const distributionListRecipients = values.selectedItems.map(item => {
      return {
        userId: item.userId,
        name: `${item.firstName} ${item.lastName}`,
        emailAddress: item.email,
        fundId: item.fundId,
        investorId: item.investors.length === 1 ? item.investors[0].id : '*',
        ccEmails: item.ccEmails
      };
    });

    const getBase64 = file => {
      return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function() {
          resolve(reader.result);
        };
      });
    };

    const attachments = await defaultTo(values.attachments, []).map(
      async item => {
        let base64Content = await getBase64(item.rawFile);
        // base64Content have form of: "mimeType;base64,fileCodedInBase64"
        let splitedRawFileData = base64Content.split(',');
        let base64FileContent = splitedRawFileData[1];
        return {
          content: base64FileContent,
          mimeType: item.type,
          fileName: item.title,
          disposition: null,
          contentId: null
        };
      }
    );

    let body = {
      distributionListRecipients,
      sendEmailDto: {
        from: {
          fromAddress: {
            name: values.alias,
            address: values.from
          },
          replyToAddress: {
            name: null,
            address: values.aliasTo
          },
          rememberEmailAddressAlias: !!values.rememberMe
        },
        to: distributionListRecipients,
        cc: splitEmails(values.cc),
        bcc: splitEmails(values.bcc),
        subject: values.subject,
        msgBody: values.msgBody,
        attachments: await Promise.all(attachments),
        isTestEmail: false
      },
      filters: dlOptions
    };

    if (distributionListId) {
      body = { ...body, distributionListId };
    }

    try {
      await DistributionListApi.sendMail(body);
      notify('ra.notification.distribution_email_sent');
    } catch (error) {
      notifyServerError(error);
    } finally {
      setSavedFormProps(null);
      setOpenBanner(false);
      setSubmitting(false);
      setIsLoading(false);
      handleClose();
    }
  };

  return (
    <Dialog
      open={showDialog}
      aria-label="Send Email to un-saved Distibution List"
      maxWidth={false}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        handleClose();
      }}
      disableEnforceFocus={true}
      classes={{
        paperWidthFalse: classes.paperWidthFalse // class name, e.g. `classes-nesting-root-x`
      }}
    >
      <SimpleFormWithSave
        toolbar={null}
        resource="distributionlists"
        onSave={handleOpenBanner}
        variant="standard"
        margin="normal"
        className={classes.form}
        initialValues={{
          selectedItems: initRecipients,
          templateId: templateId
        }}
      >
        <FormSpy subscription={{ pristine: true }}>
          {formProps => {
            return (
              <>
                <DialogTitleWithCloseButton onClose={handleClose}>
                  Edit Email
                </DialogTitleWithCloseButton>
                <DialogContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <EmailForm
                        variant="standard"
                        choices={choices}
                        groupBy="fundId"
                        groupLabel="fundName"
                        recipientId={['userId', 'fundId']}
                        textButton={distributionListName}
                        attachments={true}
                        variableType={variableType}
                        userProfile={userProfile}
                        useFundFromEmail={false}
                        useManualEmailFrom={true}
                      />
                    </Grid>
                  </Grid>
                  <FormDataConsumer>
                    {({ formData, ...rest }) =>
                      formData && (
                        <DistributionListContactDialog
                          title=" "
                          showDialog={showDistributionListTestEmailDialog}
                          handleDialogToggle={
                            setShowDistributionListTestEmailDialog
                          }
                          formData={formData}
                          distributionListId={distributionListId}
                          filters={dlOptions}
                          {...formProps}
                        />
                      )
                    }
                  </FormDataConsumer>
                  <FormDataConsumer>
                    {({ formData, ...rest }) =>
                      formData && (
                        <EmailPreviewDialog
                          title=" "
                          showDialog={showDistributionListPreviewEmailDialog}
                          handleDialogToggle={() =>
                            setShowDistributionListPreviewEmailDialog(
                              !showDistributionListPreviewEmailDialog
                            )
                          }
                          formData={formData}
                          resourceUploadId={getResourceUploadId(dlOptions)}
                          {...formProps}
                        />
                      )
                    }
                  </FormDataConsumer>
                </DialogContent>
                <DialogActions>
                  <div className={classes.leftWrapper}>
                    <Button
                      variant="outlined"
                      color="primary"
                      className={classes.primaryButton}
                      onClick={() =>
                        setShowDistributionListTestEmailDialog(
                          !showDistributionListTestEmailDialog
                        )
                      }
                    >
                      Test Email
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      className={classes.secondaryButton}
                      onClick={() =>
                        setShowDistributionListPreviewEmailDialog(
                          !showDistributionListPreviewEmailDialog
                        )
                      }
                    >
                      Preview Email
                    </Button>
                  </div>
                  <SaveButton
                    saving={submitting}
                    handleSubmitWithRedirect={() => handleSendClick(formProps)}
                    color="primary"
                    disabled={submitting}
                    variant="contained"
                    className={classes.button}
                    label={'Send Email'}
                    icon={<></>}
                  />
                </DialogActions>
                <Confirm
                  isOpen={openBanner}
                  loading={isLoading}
                  title="Warning"
                  content={
                    <p style={{ margin: 0 }}>
                      More than one investor has been selected.
                      <br />
                      Please make sure that this email and attachments are
                      targeted to the intended recipients.
                    </p>
                  }
                  onConfirm={() => handleSubmit(savedFormProps)}
                  onClose={handleCloseBanner}
                  confirm="Send"
                />
              </>
            );
          }}
        </FormSpy>
      </SimpleFormWithSave>
    </Dialog>
  );
};

export default EmailDialog;
